/**
 * useInProgressReconnect · 刷新后接上后台还在跑的 agent
 *
 * 设计动机（参考豆包等流式 AI 产品的"刷新接上"）:
 *
 *   用户和 agent 对话中刷新页面 → SSE 流断了 → 但后端 agent.Run 是 detached ctx
 *   不会被取消，会继续跑完落 status=OK。期间持续节流 UPDATE 已生成的 assistant
 *   _response（progressPersister 每 1.5s 一次）。
 *
 *   前端 useAIClassifySession 拉 history 时若 status=4 (InProgress) 会渲染成
 *   streaming-style 占位（text 显示当前累积内容 + spinner）。本 hook 启动轮询
 *   每 1.5s 拉一次 history 拿到最新 assistant_response 同步到 redux，让用户看
 *   到伪流式补齐效果，直到 status 变 done/error 自动停轮询。
 *
 * 触发条件：
 *
 *   messages 列表里存在「id 以 hist-asst- 开头 + status === 'streaming'」的消息
 *   （即从 history 加载来的且后端标 InProgress）。当前 SSE 在跑的（id 不以
 *   hist-asst- 开头）不触发轮询，避免重复抢流。
 *
 * 停止条件：
 *
 *   · status 变 'done' / 'error'（agent 跑完或真错）
 *   · 持续 2 分钟仍 streaming（agent 卡死兜底，停轮询并标 error）
 *   · conversation 切换 / 组件卸载
 */

import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import {
  assistantHydratedFromHistory,
  selectAllMessages
} from '../../../slices/aiClassify'
import {
  fetchAiChatHistory,
  transformBackendMessage
} from '../services/aiChatHistory'

const POLL_INTERVAL_MS = 1500
// 「无死值超时」方案 (详见 docs §08): 前端不再自计时判超时, 只跟随后端 status。
// 后端 ai-fc 内部有 AgentRunMaxDuration 兜底防真死锁, 真超时会落 status=failed/canceled,
// 前端 history 拿到自动标 error。前端唯一兜底是「连续网络错误」(防 history 接口挂):
// 连续 N 次拉 history 网络失败才算真异常 → 报"网络异常"而非"任务超时", 不误导用户。
const MAX_CONSECUTIVE_NETWORK_ERRORS = 5

interface Params {
  conversationId: string
  groupID: string
}

export function useInProgressReconnect({
  conversationId,
  groupID
}: Params): void {
  const dispatch = useAppDispatch()
  const messages = useAppSelector(selectAllMessages)

  // 找最后一条「history 来的 + 还在 streaming」的消息
  // 用 backendMessageId 做 key（history 转换时已经存到 message 对象上）
  let pendingId: string | null = null
  let pendingBackendId: number | null = null
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.kind !== 'assistant') continue
    if (!m.id.startsWith('hist-asst-')) continue
    if (m.status !== 'streaming') continue
    pendingId = m.id
    pendingBackendId =
      typeof m.backendMessageId === 'number' ? m.backendMessageId : null
    break
  }

  useEffect(() => {
    if (!pendingId || !pendingBackendId || !conversationId || !groupID) return

    let timer: number | null = null
    let stopped = false
    let consecutiveNetErrors = 0

    const tick = async (): Promise<void> => {
      if (stopped) return
      try {
        const histResp = await fetchAiChatHistory(conversationId, groupID)
        if (stopped) return
        consecutiveNetErrors = 0 // 拉到了就重置网络错误计数
        // 新协议 message 顶层 message_id (业务命名, 与接口 5 入参对齐) 与 id (兼容字段)
        // 服务端保证同值, 这里两者都比对以容忍后端字段渐进迁移。
        const updated = histResp.messages.find(
          (m) =>
            (m.message_id ?? m.id) === pendingBackendId ||
            m.id === pendingBackendId
        )
        if (!updated) {
          // 这条消息从 history 消失了（比如后端落了 Canceled 被过滤）→ 标 error 停止
          dispatch(
            assistantHydratedFromHistory({
              messageId: pendingId!,
              text: '',
              status: 'error',
              errorMessage: '上次任务被中断'
            })
          )
          return
        }
        if (updated.data == null) {
          // 协议约定 data == null 不可渲染, 但本轮拉到的 updated 自己缺 data 一般意味着
          // 后端还没写完 (InProgress 中间态), 继续下一轮轮询而不是误标错误
          timer = window.setTimeout(tick, POLL_INTERVAL_MS)
          return
        }
        // 用 transformBackendMessage 拿到统一的 status / text / payload，
        // 然后整体覆盖到现有 message（id 保持 hist-asst-{m.id} 不变）
        const { assistant } = transformBackendMessage(updated)
        // 后端 history 接口已带 tools_progress (建了 N/M + ETA), 让 UI 实时渲染进度。
        // TODO(前端): 把 (updated as any).tools_progress 透传到 redux store,
        // UI 加进度条组件订阅该字段渲染 "建了 17/24 · 预计还需 12s"。
        // 当前先保证核心 — 删 MAX_DURATION_MS 死超时 — 已生效。
        dispatch(
          assistantHydratedFromHistory({
            messageId: pendingId!,
            text: assistant.text,
            payload: assistant.payload,
            status: assistant.status,
            errorMessage: assistant.errorMessage
          })
        )
        if (assistant.status === 'streaming') {
          // 仍 InProgress → 继续轮询, 不自计时, status 变了才停
          timer = window.setTimeout(tick, POLL_INTERVAL_MS)
        }
        // status === 'done' / 'error' → 自动停（不再 schedule）
      } catch {
        consecutiveNetErrors++
        if (consecutiveNetErrors >= MAX_CONSECUTIVE_NETWORK_ERRORS) {
          // 连续网络错误兜底: history 接口挂了 → 报"网络异常"(不报"任务超时", 避免误导)
          dispatch(
            assistantHydratedFromHistory({
              messageId: pendingId!,
              text: '',
              status: 'error',
              errorMessage: '网络异常，请检查网络后刷新重试'
            })
          )
          return
        }
        // 网络抖一下不致命，下次再试
        timer = window.setTimeout(tick, POLL_INTERVAL_MS)
      }
    }

    // 立即跑一次（用户进来 1.5s 内有反馈）
    void tick()

    return () => {
      stopped = true
      if (timer != null) window.clearTimeout(timer)
    }
  }, [pendingId, pendingBackendId, conversationId, groupID, dispatch])
}
